// ============================================
// Event Settlements Management
// ============================================

import React from 'react';
import { collection, onSnapshot, doc, updateDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { Event, Settlement } from '../../../types';

interface Props {
  event: Event;
}

type FilterStatus = 'all' | 'pending' | 'confirmed';

export default function EventSettlements({ event }: Props) {
  const { user } = useAuth();
  const [settlements, setSettlements] = React.useState<Settlement[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [filterStatus, setFilterStatus] = React.useState<FilterStatus>('pending');
  const [confirmingId, setConfirmingId] = React.useState<string | null>(null);

  // Subscribe to settlements
  React.useEffect(() => {
    const settlementsQuery = query(
      collection(db, 'events', event.id, 'settlements'),
      orderBy('date', 'desc')
    );

    const unsubscribe = onSnapshot(settlementsQuery, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Settlement[];
      setSettlements(data);
      setLoading(false);
    });

    return unsubscribe;
  }, [event.id]);

  // Confirm a settlement
  const confirmSettlement = async (settlement: Settlement) => {
    if (!user) return;

    const confirmed = window.confirm(
      `Confirm receipt of €${settlement.eventPayout.toFixed(2)} from ${settlement.therapistName} for ${settlement.date}?`
    );
    if (!confirmed) return;

    setConfirmingId(settlement.id);
    try {
      await updateDoc(doc(db, 'events', event.id, 'settlements', settlement.id), {
        status: 'confirmed',
        confirmedAt: serverTimestamp(),
        confirmedBy: user.uid,
      });
    } catch (err) {
      console.error('Error confirming settlement:', err);
      alert('Failed to confirm settlement');
    } finally {
      setConfirmingId(null);
    }
  };

  // Filter settlements
  const filteredSettlements = settlements.filter((s) => {
    if (filterStatus === 'all') return true;
    return s.status === filterStatus;
  });

  // Group by date for easier viewing
  const groupedByDate = filteredSettlements.reduce((acc, settlement) => {
    if (!acc[settlement.date]) {
      acc[settlement.date] = [];
    }
    acc[settlement.date].push(settlement);
    return acc;
  }, {} as Record<string, Settlement[]>);

  // Calculate totals
  const pendingTotal = settlements
    .filter((s) => s.status === 'pending')
    .reduce((sum, s) => sum + s.eventPayout, 0);

  const confirmedTotal = settlements
    .filter((s) => s.status === 'confirmed')
    .reduce((sum, s) => sum + s.eventPayout, 0);

  if (loading) {
    return <div className="loading">Loading settlements...</div>;
  }

  return (
    <div className="event-settlements">
      <div className="section-header" style={{ marginBottom: '1rem' }}>
        <h2>Settlements</h2>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{
          flex: '1 1 200px',
          padding: '1rem',
          borderRadius: '8px',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          border: '1px solid #f59e0b',
        }}>
          <div style={{ fontSize: '0.875rem', color: '#f59e0b', marginBottom: '0.25rem' }}>Pending</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f59e0b' }}>
            €{pendingTotal.toFixed(2)}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
            {settlements.filter((s) => s.status === 'pending').length} settlement(s)
          </div>
        </div>
        <div style={{
          flex: '1 1 200px',
          padding: '1rem',
          borderRadius: '8px',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          border: '1px solid #22c55e',
        }}>
          <div style={{ fontSize: '0.875rem', color: '#22c55e', marginBottom: '0.25rem' }}>Confirmed</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#22c55e' }}>
            €{confirmedTotal.toFixed(2)}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
            {settlements.filter((s) => s.status === 'confirmed').length} settlement(s)
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {(['pending', 'confirmed', 'all'] as FilterStatus[]).map((status) => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: filterStatus === status ? 'var(--color-primary)' : 'var(--color-bg-secondary)',
              color: filterStatus === status ? '#fff' : 'var(--color-text)',
              cursor: 'pointer',
              fontWeight: filterStatus === status ? 600 : 400,
              textTransform: 'capitalize',
            }}
          >
            {status}
          </button>
        ))}
      </div>

      {/* Settlements List */}
      {filteredSettlements.length === 0 ? (
        <div className="empty-state">
          <p>No {filterStatus !== 'all' ? filterStatus : ''} settlements yet.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {Object.entries(groupedByDate)
            .sort(([a], [b]) => b.localeCompare(a))
            .map(([date, daySettlements]) => (
              <div key={date}>
                <div style={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'var(--color-text-secondary)',
                  marginBottom: '0.5rem',
                  paddingLeft: '0.5rem',
                }}>
                  {new Date(date + 'T12:00:00').toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'short',
                    day: 'numeric',
                  })}
                </div>
                {daySettlements.map((settlement) => (
                  <div
                    key={settlement.id}
                    style={{
                      padding: '1rem',
                      borderRadius: '8px',
                      backgroundColor: 'var(--color-bg-secondary)',
                      border: `1px solid ${settlement.status === 'confirmed' ? '#22c55e' : '#f59e0b'}`,
                      marginBottom: '0.5rem',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '1rem' }}>{settlement.therapistName}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                          {settlement.totalSessions} sessions • {settlement.totalMinutes} min
                        </div>
                      </div>
                      <div style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        backgroundColor: settlement.status === 'confirmed' ? '#22c55e' : '#f59e0b',
                        color: '#fff',
                      }}>
                        {settlement.status}
                      </div>
                    </div>

                    {/* Financial Details */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
                      gap: '0.5rem',
                      fontSize: '0.875rem',
                      marginBottom: '0.75rem',
                    }}>
                      <div>
                        <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.75rem' }}>Cash Collected</div>
                        <div style={{ fontWeight: 600 }}>€{settlement.totalAmountReceived.toFixed(2)}</div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.75rem' }}>Therapist Keep</div>
                        <div style={{ fontWeight: 600, color: 'var(--color-primary)' }}>€{settlement.therapistPayout.toFixed(2)}</div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.75rem' }}>Tips</div>
                        <div style={{ fontWeight: 600 }}>€{settlement.totalTips.toFixed(2)}</div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.75rem' }}>Event Share</div>
                        <div style={{ fontWeight: 700, color: '#f59e0b' }}>€{settlement.eventPayout.toFixed(2)}</div>
                      </div>
                    </div>

                    {/* Confirm Button */}
                    {settlement.status === 'pending' && (
                      <button
                        onClick={() => confirmSettlement(settlement)}
                        disabled={confirmingId === settlement.id}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          backgroundColor: '#22c55e',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          opacity: confirmingId === settlement.id ? 0.7 : 1,
                        }}
                      >
                        {confirmingId === settlement.id ? 'Confirming...' : `Confirm €${settlement.eventPayout.toFixed(2)} Received`}
                      </button>
                    )}

                    {settlement.status === 'confirmed' && settlement.confirmedAt && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', textAlign: 'center' }}>
                        Confirmed {settlement.confirmedAt.toDate?.()?.toLocaleString() || ''}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
